import {
  Button,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
} from '@material-ui/core';
import {Edit} from '@material-ui/icons';
import {DatePicker} from '@material-ui/pickers';
import {
  InvalidTaxIdentificationNumber,
  TaxIdentificationNumber,
  ValidTaxIdentificationNumber,
} from 'Agent/Policies/models/TaxIdentificationNumber';
import {updateBeneficiaryDetails} from 'Beneficiaries/ducks/beneficiaryDetail';
import {
  BeneficiaryPerson,
  EmptyPersonType,
  InvalidBeneficiaryPersonDateOfBirth,
  ValidBeneficiaryPersonDateOfBirth,
  ValidPersonType,
} from 'Beneficiaries/models/BeneficiaryPerson';
import {ContingentBeneficiary, PersonTypes, PrimaryBeneficiary} from 'Beneficiaries/models/PersonTypes';
import {Loaded} from 'Core/models/Async';
import {InvalidEmail, ValidEmail} from 'Core/models/Email';
import {EmptyString, NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import {InvalidPhoneNumber, ValidPhoneNumber} from 'Core/models/PhoneNumber';
import {Eq} from 'fp-ts/lib/Eq';
import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {toFormattedDate} from 'Shared/utilities/DateFormatter';
import {maskPhoneNumber, unmaskPhoneNumber} from 'Shared/utilities/phoneNumberMasker';
import {maskTaxId, unMaskTaxId} from 'Shared/utilities/taxIdMasker';
import './WalmartBeneficiaryForm.sass';

type WalmartBeneficiaryFormProps = {
  beneficiaryPerson: BeneficiaryPerson;
  originalBeneficiaryPerson: BeneficiaryPerson;
  onFieldChange: (field: {[key in keyof BeneficiaryPerson]: BeneficiaryPerson[key]}) => void;
  percentageError: boolean;
  index: number;
};

const WalmartBeneficiaryForm = ({
  beneficiaryPerson,
  originalBeneficiaryPerson,
  onFieldChange,
  percentageError,
  index,
}: WalmartBeneficiaryFormProps) => {
  const dispatch = useDispatch();
  const {beneficiaryDetails} = useSelector((state: ReducerState) => state.beneficiaryDetail);

  const equalIdentifier: Eq<TaxIdentificationNumber | StringUnion | undefined> = {
    equals: (x, y) => x?.value === y?.value,
  };

  const equalSignatureDate: Eq<Date | undefined> = {
    equals: (x, y) => x === y,
  };

  const equalSourceSystemIdentifier: Eq<NonEmptyString | EmptyString | undefined> = {
    equals: (x, y) => x?.value === y?.value,
  };

  const shouldDisableIdentifierField = (identifier?: TaxIdentificationNumber | StringUnion) =>
    equalIdentifier.equals(identifier, originalBeneficiaryPerson?.identifier) &&
    !(identifier instanceof EmptyString) &&
    !!beneficiaryPerson.id;

  const fieldIsRequiredErrorMessage = 'Field is Required';
  const percentageDoesNotAddUpTo100ErrorMessage = 'Percentages does not add up to 100';
  const isInsert = !beneficiaryPerson.id;

  // If person is of type beneficiary, then allow add or delete, as well as all editing
  const isBeneficiary = (personTypeId?: number) =>
    personTypeId === PrimaryBeneficiary.id || personTypeId === ContingentBeneficiary.id;

  // If user is not inserting new record and the selected person type is not a beneficiary, then only allow editing on phone number and addresses.
  // This is due to L70 only have info for phone number and address, we need to be consistant.
  const onlyAllowPhoneAndAddressEditing =
    (!isInsert && !isBeneficiary(beneficiaryPerson.personType?.id)) || beneficiaryPerson.markedAsDeleted;

  return (
    <Grid container alignItems="center" direction="column">
      <form className="walmart_beneficiary_form" noValidate>
        <Grid container spacing={4}>
          <Grid item xs={12} md={3}>
            <FormControl variant="outlined" fullWidth>
              <InputLabel
                htmlFor={`identifier-input_${index}`}
                error={beneficiaryPerson.identifier instanceof InvalidTaxIdentificationNumber}
              >
                SSN
              </InputLabel>
              <OutlinedInput
                id={`identifier-input_${index}`}
                fullWidth
                placeholder="XXX-XX-XXXX"
                labelWidth={35}
                inputProps={{
                  maxLength: 11,
                }}
                disabled={shouldDisableIdentifierField(beneficiaryPerson.identifier) || onlyAllowPhoneAndAddressEditing}
                value={maskTaxId(beneficiaryPerson.identifier?.value) || ''}
                endAdornment={
                  shouldDisableIdentifierField(beneficiaryPerson.identifier) &&
                  !onlyAllowPhoneAndAddressEditing && (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="editing SSN"
                        onClick={() => onFieldChange({identifier: ValidTaxIdentificationNumber.create('')})}
                        edge="end"
                      >
                        <Edit color="primary" />
                      </IconButton>
                    </InputAdornment>
                  )
                }
                onChange={e =>
                  onFieldChange({identifier: ValidTaxIdentificationNumber.create(unMaskTaxId(e.target.value))})
                }
                error={beneficiaryPerson.identifier instanceof InvalidTaxIdentificationNumber}
              />
              {beneficiaryPerson.identifier instanceof InvalidTaxIdentificationNumber && (
                <FormHelperText id="identifer-helper-text" error>
                  {beneficiaryPerson.identifier.error}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              variant="outlined"
              label="First Name"
              value={beneficiaryPerson.firstName || ''}
              onChange={e => onFieldChange({firstName: e.target.value})}
              disabled={onlyAllowPhoneAndAddressEditing}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              variant="outlined"
              label="Middle Name"
              value={beneficiaryPerson.middleName || ''}
              onChange={e => onFieldChange({middleName: e.target.value})}
              disabled={onlyAllowPhoneAndAddressEditing}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              variant="outlined"
              label="Last Name"
              value={beneficiaryPerson.lastName || ''}
              onChange={e => onFieldChange({lastName: e.target.value})}
              disabled={onlyAllowPhoneAndAddressEditing}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              variant="outlined"
              label="Relationship"
              value={beneficiaryPerson.relationship || ''}
              onChange={e => onFieldChange({relationship: e.target.value})}
              disabled={onlyAllowPhoneAndAddressEditing}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <DatePicker
              fullWidth
              variant="inline"
              inputVariant="outlined"
              color="primary"
              label="Date of Birth"
              format="MM/dd/yyyy"
              maxDate={new Date()}
              value={beneficiaryPerson.dateOfBirth?.value || null}
              onChange={date =>
                onFieldChange({
                  dateOfBirth: ValidBeneficiaryPersonDateOfBirth.create(
                    date ? toFormattedDate(date.toString()) : undefined
                  ),
                })
              }
              disabled={onlyAllowPhoneAndAddressEditing}
              error={beneficiaryPerson.dateOfBirth instanceof InvalidBeneficiaryPersonDateOfBirth}
              helperText={
                beneficiaryPerson.dateOfBirth instanceof InvalidBeneficiaryPersonDateOfBirth &&
                beneficiaryPerson.dateOfBirth.validationError
              }
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              placeholder="(XXX)-XXX XXXX"
              inputProps={{
                maxLength: 14,
              }}
              variant="outlined"
              label="Phone Number"
              disabled={beneficiaryPerson.markedAsDeleted}
              value={maskPhoneNumber(beneficiaryPerson.phoneNumber?.phoneNumber) || ''}
              onChange={e => onFieldChange({phoneNumber: ValidPhoneNumber.create(unmaskPhoneNumber(e.target.value))})}
              error={beneficiaryPerson.phoneNumber instanceof InvalidPhoneNumber}
              helperText={
                beneficiaryPerson.phoneNumber instanceof InvalidPhoneNumber &&
                beneficiaryPerson.phoneNumber.validationError
              }
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              variant="outlined"
              label="Gender"
              value={beneficiaryPerson.gender || ''}
              onChange={e => onFieldChange({gender: e.target.value})}
              disabled={onlyAllowPhoneAndAddressEditing}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl required variant="outlined" fullWidth>
              <InputLabel id="person-type-label">Person Type</InputLabel>
              <Select
                labelId="person-type-label"
                value={beneficiaryPerson.personType?.id || ''}
                onChange={e => onFieldChange({personType: ValidPersonType.create(e.target.value as number)})}
                disabled={onlyAllowPhoneAndAddressEditing}
                labelWidth={105}
                required
                error={beneficiaryPerson.personType instanceof EmptyPersonType}
              >
                {PersonTypes.map(personType => (
                  <MenuItem key={personType.id} value={personType.id} disabled={!isBeneficiary(personType.id)}>
                    {personType.name}
                  </MenuItem>
                ))}
              </Select>
              {beneficiaryPerson.personType instanceof EmptyPersonType && (
                <FormHelperText error>{fieldIsRequiredErrorMessage}</FormHelperText>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              variant="outlined"
              label="Email"
              value={beneficiaryPerson.email?.address || ''}
              onChange={e => onFieldChange({email: ValidEmail.create(e.target.value)})}
              disabled={onlyAllowPhoneAndAddressEditing}
              error={beneficiaryPerson.email instanceof InvalidEmail}
              helperText={beneficiaryPerson.email instanceof InvalidEmail && beneficiaryPerson.email.validationError}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <DatePicker
              fullWidth
              variant="inline"
              inputVariant="outlined"
              color="primary"
              label="Signature Date"
              format="MM/dd/yyyy"
              maxDate={new Date()}
              value={`${beneficiaryPerson.signatureDate || '01-01-1900'}`}
              onChange={date =>
                onFieldChange({
                  signatureDate: date ? new Date(toFormattedDate(date.toString())) : undefined,
                })
              }
              required={!onlyAllowPhoneAndAddressEditing}
              disabled={onlyAllowPhoneAndAddressEditing}
              error={
                !onlyAllowPhoneAndAddressEditing &&
                !equalSignatureDate.equals(beneficiaryPerson.signatureDate, originalBeneficiaryPerson?.signatureDate) &&
                !beneficiaryPerson.signatureDate
              }
              helperText={
                !onlyAllowPhoneAndAddressEditing &&
                !equalSignatureDate.equals(beneficiaryPerson.signatureDate, originalBeneficiaryPerson?.signatureDate) &&
                !beneficiaryPerson.signatureDate &&
                fieldIsRequiredErrorMessage
              }
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              type="number"
              InputProps={{inputProps: {min: 0, max: 100, step: 1}}}
              variant="outlined"
              label="Beneficiary Percentage"
              value={beneficiaryPerson.beneficiaryPercent || 0}
              onChange={e => onFieldChange({beneficiaryPercent: parseInt(e.target.value, 10) || 0})}
              required
              disabled={onlyAllowPhoneAndAddressEditing}
              error={percentageError}
              helperText={percentageError && percentageDoesNotAddUpTo100ErrorMessage}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              variant="outlined"
              label="Source System Identifier"
              value={beneficiaryPerson.sourceSystemIdentifier?.value || ''}
              onChange={e => onFieldChange({sourceSystemIdentifier: NonEmptyString.create(e.target.value)})}
              required
              disabled={onlyAllowPhoneAndAddressEditing}
              error={
                beneficiaryPerson.sourceSystemIdentifier instanceof EmptyString &&
                !equalSourceSystemIdentifier.equals(
                  beneficiaryPerson.sourceSystemIdentifier,
                  originalBeneficiaryPerson?.sourceSystemIdentifier
                )
              }
              helperText={
                beneficiaryPerson.sourceSystemIdentifier instanceof EmptyString &&
                !equalSourceSystemIdentifier.equals(
                  beneficiaryPerson.sourceSystemIdentifier,
                  originalBeneficiaryPerson?.sourceSystemIdentifier
                ) &&
                fieldIsRequiredErrorMessage
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              variant="outlined"
              label="Address 1"
              disabled={beneficiaryPerson.markedAsDeleted}
              value={beneficiaryPerson.address1 || ''}
              onChange={e => onFieldChange({address1: e.target.value})}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              variant="outlined"
              label="Address 2"
              disabled={beneficiaryPerson.markedAsDeleted}
              value={beneficiaryPerson.address2 || ''}
              onChange={e => onFieldChange({address2: e.target.value})}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              variant="outlined"
              label="Address 3"
              disabled={beneficiaryPerson.markedAsDeleted}
              value={beneficiaryPerson.address3 || ''}
              onChange={e => onFieldChange({address3: e.target.value})}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              variant="outlined"
              label="City"
              disabled={beneficiaryPerson.markedAsDeleted}
              value={beneficiaryPerson.city || ''}
              onChange={e => onFieldChange({city: e.target.value})}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              variant="outlined"
              label="State"
              disabled={beneficiaryPerson.markedAsDeleted}
              value={beneficiaryPerson.state || ''}
              onChange={e => onFieldChange({state: e.target.value})}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              variant="outlined"
              label="Zip Code"
              disabled={beneficiaryPerson.markedAsDeleted}
              value={beneficiaryPerson.zipcode || ''}
              onChange={e => onFieldChange({zipcode: e.target.value})}
            />
          </Grid>
          {isInsert && (
            <Grid item container justify="flex-end">
              <Button
                color="primary"
                onClick={() =>
                  dispatch(
                    updateBeneficiaryDetails(
                      new Loaded((beneficiaryDetails as Loaded<BeneficiaryPerson[]>).Data.filter((p, i) => i !== index))
                    )
                  )
                }
              >
                Remove
              </Button>
            </Grid>
          )}
        </Grid>
      </form>
    </Grid>
  );
};

export default WalmartBeneficiaryForm;
